import { Controller } from "@hotwired/stimulus"

// Client-side idle detection. Companions the server-side
// SessionAutoLock concern: when the user is idle for
// `timeout-minutes`, we send them to /admin/lock without waiting
// for their next request, so they don't have to click into a now-
// locked page before discovering it.
//
// Server-side lock is still the source of truth — this controller
// is a UX nicety. Any request the user makes after the timeout
// would be redirected anyway.
//
// Usage:
//   <body data-controller="auto-lock"
//         data-auto-lock-timeout-minutes-value="15"
//         data-auto-lock-lock-url-value="/admin/lock">
//
// Set timeout-minutes-value to 0 to disable (e.g. the account
// hasn't enabled auto-lock, or we're on a non-admin path). The
// controller installs its listeners regardless and reacts via the
// timeoutMinutesValueChanged callback if the value flips later
// (e.g. Turbo nav from a sign-in page → an admin page swaps the
// body's data attribute, but Stimulus does NOT re-run connect()
// for an already-mounted controller — only the value-changed
// callback fires).
export default class extends Controller {
  static values = {
    timeoutMinutes: { type: Number, default: 0 },
    lockUrl: { type: String, default: "/admin/lock" }
  }

  connect() {
    this.boundReset = this.resetTimer.bind(this)
    this.boundVisibility = this.onVisibilityChange.bind(this)
    this.events.forEach((ev) => window.addEventListener(ev, this.boundReset, { passive: true }))
    document.addEventListener("visibilitychange", this.boundVisibility)
    // resetTimer is also called by timeoutMinutesValueChanged on initial
    // connect (Stimulus fires the value-changed callback once after
    // connect with the initial value), so we don't need to call it here.
  }

  disconnect() {
    this.events.forEach((ev) => window.removeEventListener(ev, this.boundReset))
    document.removeEventListener("visibilitychange", this.boundVisibility)
    this.clearTimer()
  }

  // Fires on initial connect AND whenever the body's data attribute
  // changes (e.g. Turbo Drive copying attrs from a newly-rendered body).
  // This is the only way to react to value changes after connect.
  timeoutMinutesValueChanged() {
    this.timeoutMs = this.timeoutMinutesValue > 0 ? this.timeoutMinutesValue * 60 * 1000 : null
    this.resetTimer()
  }

  get events() {
    return [ "mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel" ]
  }

  resetTimer() {
    this.clearTimer()
    if (!this.timeoutMs) return
    this.timer = setTimeout(() => this.lock(), this.timeoutMs)
  }

  clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  // When the user comes back to the tab, check whether enough time
  // passed while it was hidden that we should already be locked.
  // (setTimeout pauses in some browsers when the tab is backgrounded.)
  onVisibilityChange() {
    if (document.visibilityState !== "visible") {
      this.hiddenAt = Date.now()
      return
    }
    if (!this.hiddenAt) return
    const awayMs = Date.now() - this.hiddenAt
    this.hiddenAt = null
    if (this.timeoutMs && awayMs >= this.timeoutMs) this.lock()
  }

  lock() {
    // Avoid bouncing if we're already on the lock screen.
    if (window.location.pathname === this.lockUrlValue) return
    window.location.href = this.lockUrlValue
  }
}
